/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { useState, useEffect, Fragment } from "react";

import { API } from "aws-amplify";
import { updateInspectionResult } from "src/graphql/mutations";

import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

import BoltOptions from "src/components/slideovers/BoltOptions.react";

/**
 * The EditBoltResult component
 *
 * @author Thais Watanabe
 */
const EditBoltResult = (props) => {
  const { isEditBoltOpen, updateEdit } = props;

  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const [editedChildResults, setEditedChildResults] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");
  const [changeObs, setChangeObs] = useState("");
  const [hasErrors, setHasErrors] = useState(false);

  /**
   * useEffect
   */
  useEffect(() => {
    if (props.inspectionModelItem) {
      setSelectedItem(props.inspectionModelItem);
    }

    if (props.inspectionResult) {
      setSelectedResult(props.inspectionResult);
      setSelectedOption(props.inspectionResult.result);
      setChangeObs(
        props.inspectionResult.changeObs ? props.inspectionResult.changeObs : ""
      );
    }
    setHasErrors(false);
  }, [props]);

  /**
   * useEffect
   */
  useEffect(() => {
    setSelectedResult(props.inspectionResult);
    if (props.inspectionResult) {
      setEditedChildResults(
        props.inspectionResult.customResults
          ? props.inspectionResult.customResults
          : []
      );
      setSelectedOption(props.inspectionResult.result);
      setChangeObs(
        !props.inspectionResult.changeObs
          ? ""
          : props.inspectionResult.changeObs
      );
    }
    setHasErrors(false);
  }, [props]);

  /**
   * onClose
   */
  function onClose() {
    updateEdit(!isEditBoltOpen);
    // updateItem(null);
    setSelectedItem(null);
  }

  /**
   * setChildrenResults
   * @param {*} position
   * @param {*} value
   */
  function setChildrenResults(position, value) {
    let newChild = [...editedChildResults];
    newChild[position] = value;
    setEditedChildResults(newChild);
  }

  /**
   * submit
   */
  async function submit() {
    var error = false;

    if (changeObs === "" || changeObs.length < 1) {
      setHasErrors(true);
      error = true;
    } else {
      setHasErrors(false);
      error = false;
    }

    if (!error) {
      selectedResult.result = selectedOption;
      selectedResult.changeObs = changeObs;
      selectedResult.customResults = editedChildResults;
      selectedResult.updatedAt = new Date().toISOString();

      const { _deleted, _lastChangedAt, ...newResult } = selectedResult;

      try {
        await API.graphql({
          query: updateInspectionResult,
          variables: { input: newResult },
        });

        toast.success("保存しました");
        updateEdit(!isEditBoltOpen);
      } catch (err) {
        console.log(err);
        toast.error("エラーが発生しました");
      }
    }
  }

  return (
    <Transition.Root show={isEditBoltOpen} as={Fragment}>
      <Dialog
        as="div"
        static
        className="z-50 fixed inset-0 overflow-hidden"
        open={isEditBoltOpen}
        onClose={() => onClose()}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0" />

          <div className="fixed inset-y-0 pl-16 max-w-full right-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="w-screen max-w-md">
                <div className="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl">
                  <div className="flex-1 h-0 overflow-y-auto">
                    <div className="py-6 px-4 bg-gray-300 sm:px-6">
                      <div className="flex items-center justify-between">
                        <Dialog.Title className="text-lg font-medium text-gray-900">
                          修正する
                        </Dialog.Title>
                        <div className="ml-3 h-7 flex items-center">
                          <button
                            type="button"
                            className="bg-gray-300 rounded-md text-gray-900 hover:text-gray-500 focus:outline-none"
                            onClick={() => onClose()}
                          >
                            <span className="sr-only">閉める</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>
                    {!selectedItem && (
                      <div className="flex-1 flex flex-col justify-between">
                        <div className="px-4 divide-y divide-gray-200 sm:px-6">
                          <div className="space-y-6 pt-6 pb-5">
                            <div className="loader"></div>
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedItem && (
                      <div className="flex-1 flex flex-col justify-between">
                        <div className="px-4 divide-y divide-gray-200 sm:px-6">
                          <div className="space-y-6 pt-6 pb-5">
                            <div>
                              <h3 className="text-lg leading-6 font-medium text-gray-900">
                                {selectedItem.name +
                                  " - " +
                                  selectedItem.description}
                              </h3>
                            </div>

                            {selectedResult?.customResults?.map(
                              (childResult, i) => (
                                <BoltOptions
                                  key={i}
                                  child={childResult}
                                  position={i}
                                  setChildrenResults={setChildrenResults}
                                  editedChildResults={editedChildResults}
                                  selectedOption={selectedOption}
                                />
                              )
                            )}

                            <div>
                              <label
                                htmlFor="reason"
                                className="block text-sm font-medium text-gray-900"
                              >
                                修正理由
                              </label>
                              <div className="mt-1">
                                <textarea
                                  id="reason"
                                  name="reason"
                                  rows="4"
                                  className={`${
                                    hasErrors
                                      ? "pr-10 border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-50"
                                      : "focus:ring-teal-500 focus:border-teal-500 border-gray-300"
                                  } block w-full shadow-sm sm:text-sm rounded-md`}
                                  value={changeObs}
                                  onChange={(e) => setChangeObs(e.target.value)}
                                ></textarea>
                              </div>
                              {hasErrors && (
                                <p
                                  className="mt-2 text-sm text-red-600"
                                  id="reason-error"
                                >
                                  「修正理由」を入力してください
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 px-4 py-4 flex justify-end">
                    <button
                      type="button"
                      className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                      onClick={() => updateEdit(!isEditBoltOpen)}
                    >
                      キャンセル
                    </button>
                    <button
                      type="button"
                      className="ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                      onClick={() => submit()}
                    >
                      保存する
                    </button>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default EditBoltResult;
